/**
 * Display-masking helpers for personal contact details ("shoulder-surfing"
 * privacy on profile screens). The server returns the player's own full
 * values; these only shorten what's *rendered*, they are not a security
 * boundary.
 */

/**
 * Mask an email's local part: keep the first two characters, replace the rest
 * with a fixed run of asterisks (fixed so the rendered string doesn't leak the
 * local part's length), keep the domain. `ef****@x.com` style per the Figma
 * settings design.
 */
export function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0) return email;
  return `${email.slice(0, Math.min(2, at))}${'*'.repeat(8)}${email.slice(at)}`;
}

/**
 * Mask a phone number: keep an international prefix (`+` and up to 3 country
 * digits) and the last 4 digits, replace the middle with asterisks —
 * `+972 *****4471` style per the Figma settings design. Numbers too short to
 * have a maskable middle are returned unchanged.
 */
export function maskPhone(phone: string): string {
  const trimmed = phone.trim();
  // A separator right after "+<digits>" marks the country code explicitly
  // (`+1 (555)…`); without one (E.164, `+972523144471`) assume up to 3 digits.
  const explicitCc = /^(\+\d{1,3})[\s()-]/.exec(trimmed)?.[1];
  const compact = trimmed.replace(/[\s()-]/g, '');
  if (!/^\+?\d+$/.test(compact)) return phone;
  const prefix = explicitCc ?? /^\+\d{1,3}/.exec(compact)?.[0] ?? '';
  const rest = compact.slice(prefix.length);
  if (rest.length <= 4) return phone;
  return `${prefix}${prefix ? ' ' : ''}${'*'.repeat(rest.length - 4)}${rest.slice(-4)}`;
}
